import React, { useState } from 'react';
import { FiSend, FiX, FiBell } from 'react-icons/fi';
import { FaPaperPlane } from 'react-icons/fa';
import styled from 'styled-components';
import api from '../../../../../services/api';
import { useModal } from '../../../../../hooks/Modal';
import { useToast } from '../../../../../hooks/Toast';
import { useAuth } from '../../../../../hooks/Auth';

// =========================================================
// Templates (mesmo espelho — desta vez como select)
// =========================================================
const TEMPLATES = [
  { value: 'reminder_before_due',         label: 'Lembrete pré-vencimento' },
  { value: 'reminder_after_due:1',        label: 'Lembrete pós-vencimento (1º envio)' },
  { value: 'reminder_after_due:2',        label: 'Lembrete pós-vencimento (2º envio)' },
  { value: 'collection_letter',           label: 'Carta de cobrança' },
  { value: 'credit_card_reminder',        label: 'Lembrete cartão recorrente' },
  { value: 'team_default_notice',         label: 'Notificação à equipe — inadimplência' },
  { value: 'team_regularization',         label: 'Notificação à equipe — regularização' }
];

// =========================================================
// Styled
// =========================================================
const Container = styled.div`
  background: #fff;
  border-radius: 10px;
  min-width: 380px;
  max-width: 480px;
  width: 100%;
  color: #1f2937;
  font-family: 'Inter', -apple-system, sans-serif;
`;
const Header = styled.div`
  padding: 18px 22px;
  border-bottom: 1px solid #f3f4f6;
  display: flex; align-items: flex-start; gap: 12px;
  h3 { font-size: 16px; font-weight: 800; color: #111827; }
  p  { font-size: 12px; color: #6b7280; margin-top: 2px; }
  > div:first-child { flex: 1; }
  > button {
    background: none; border: none; cursor: pointer; color: #9ca3af;
    &:hover { color: #111827; }
  }
`;
const Body = styled.div` padding: 18px 22px; `;
const Field = styled.div` margin-bottom: 14px; `;
const Label = styled.label`
  display: block;
  font-size: 12px; font-weight: 600; color: #374151; margin-bottom: 6px;
`;
const Hint = styled.div`
  font-size: 11px; color: #6b7280; margin-top: 4px;
`;
const Input = styled.input`
  width: 100%; height: 40px; padding: 0 12px;
  border: 1.5px solid #e5e7eb; border-radius: 8px; font-size: 13px;
  font-family: inherit; background: #fff;
  &:focus { outline: none; border-color: var(--adminColor); box-shadow: 0 0 0 3px rgba(111,33,118,.1); }
`;
const Select = styled.select`
  width: 100%; height: 40px; padding: 0 12px;
  border: 1.5px solid #e5e7eb; border-radius: 8px; font-size: 13px;
  font-family: inherit; background: #fff; cursor: pointer;
  &:focus { outline: none; border-color: var(--adminColor); }
`;
const Notice = styled.div`
  background: #fffbeb; border: 1px solid #fde68a; color: #92400e;
  border-radius: 8px; padding: 10px 12px; font-size: 11px; line-height: 1.5;
  margin-bottom: 14px;
`;
const PreviewBox = styled.div`
  background: rgba(111,33,118,.06); border: 1px solid rgba(111,33,118,.15);
  border-radius: 8px; padding: 12px 14px; margin-bottom: 14px;
  font-size: 13px; color: #1f2937;
  strong { color: var(--adminColor); font-size: 18px; }
  div.sub { font-size: 11px; color: #6b7280; margin-top: 2px; }
`;
const Footer = styled.div`
  padding: 12px 22px; border-top: 1px solid #f3f4f6;
  display: flex; gap: 10px; justify-content: flex-end;
`;
const BtnCancel = styled.button`
  height: 38px; padding: 0 14px; border-radius: 8px;
  border: 1.5px solid #e5e7eb; background: #fff; color: #374151;
  font-size: 13px; font-weight: 600; cursor: pointer;
  &:hover:not(:disabled) { background: #f9fafb; }
  &:disabled { opacity: .55; cursor: not-allowed; }
`;
const BtnPrimary = styled.button`
  height: 38px; padding: 0 18px; border-radius: 8px;
  border: none; background: var(--adminColor); color: #fff;
  font-size: 13px; font-weight: 600; cursor: pointer;
  display: inline-flex; align-items: center; gap: 6px;
  &:hover:not(:disabled) { background: #5a1a60; }
  &:disabled { opacity: .55; cursor: not-allowed; }
`;

// =========================================================
// Modal — Teste (envio unitário para um e-mail)
// =========================================================
const TestModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { addToast } = useToast();
  const { handleApiErrors } = useAuth();
  const [mailType, setMailType] = useState(TEMPLATES[0].value);
  const [email, setEmail] = useState('');
  const [invoiceHash, setInvoiceHash] = useState('');
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!email) {
      addToast({ title: 'Informe um e-mail de destino', type: 'error' });
      return;
    }
    setSending(true);
    try {
      // type pode vir como "reminder_after_due:2" → separa sequence
      const [type, seqStr] = mailType.split(':');
      const sequence = seqStr ? Number(seqStr) : undefined;
      await api.post(`/invoice-mail/test/${type}`, {
        email,
        invoice_hash: invoiceHash || undefined,
        sequence
      });
      addToast({ title: 'E-mail de teste agendado', type: 'success' });
      onClose();
    } catch (err) {
      addToast({ title: 'Erro ao agendar teste', type: 'error' });
      handleApiErrors(err);
    } finally {
      setSending(false);
    }
  };

  return (
    <Container>
      <Header>
        <div>
          <h3>Enviar e-mail de teste</h3>
          <p>Dispara um dos templates de fatura para o endereço informado.</p>
        </div>
        <button onClick={onClose}><FiX size={16} /></button>
      </Header>
      <Body>
        <Notice>
          O e-mail é enviado <strong>apenas ao destinatário informado</strong>. Aluno e equipe não recebem cópia.
          Os placeholders usam os dados de uma fatura real (a primeira encontrada, ou a do hash informado).
        </Notice>

        <Field>
          <Label>Template</Label>
          <Select value={mailType} onChange={e => setMailType(e.target.value)} disabled={sending}>
            {TEMPLATES.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </Select>
        </Field>

        <Field>
          <Label>E-mail de destino</Label>
          <Input
            type="email"
            placeholder="seu.email@exemplo.com"
            value={email}
            onChange={e => setEmail(e.target.value)}
            disabled={sending}
            autoFocus
          />
        </Field>

        <Field>
          <Label>Hash da fatura (opcional)</Label>
          <Input
            type="text"
            placeholder="Use uma fatura específica como referência…"
            value={invoiceHash}
            onChange={e => setInvoiceHash(e.target.value)}
            disabled={sending}
          />
          <Hint>Se vazio, o backend pega a primeira fatura disponível como exemplo.</Hint>
        </Field>
      </Body>
      <Footer>
        <BtnCancel onClick={onClose} disabled={sending}>Cancelar</BtnCancel>
        <BtnPrimary onClick={handleSend} disabled={sending || !email}>
          <FiSend size={13} />
          {sending ? 'Enviando…' : 'Enviar teste'}
        </BtnPrimary>
      </Footer>
    </Container>
  );
};

// =========================================================
// Modal — Lote de lembrete pré-vencimento
// =========================================================
const BulkReminderModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { addToast } = useToast();
  const { handleApiErrors } = useAuth();
  const [days, setDays] = useState<number>(5);
  const [preview, setPreview] = useState<{ count: number; days: number } | null>(null);
  const [busy, setBusy] = useState<'sim' | 'send' | null>(null);

  // Alterar os dias invalida a simulação anterior (força simular de novo)
  const handleDaysChange = (v: number) => {
    setDays(v);
    setPreview(null);
  };

  // 1) Simular — roda o endpoint em dry run, só conta as faturas
  const handleSimulate = async () => {
    setBusy('sim');
    try {
      const { data } = await api.post(
        `/invoice-mail/run-reminders-before-due?dry=true&days=${days}`,
        {}
      );
      setPreview({ count: data?.count ?? 0, days });
      addToast({ title: `${data?.count ?? 0} fatura(s) na janela`, type: 'success' });
    } catch (err) {
      addToast({ title: 'Erro ao simular', type: 'error' });
      handleApiErrors(err);
    } finally {
      setBusy(null);
    }
  };

  // 2) Enviar — só habilita depois de simular os mesmos dias
  const handleSend = async () => {
    setBusy('send');
    try {
      const { data } = await api.post(
        `/invoice-mail/run-reminders-before-due?days=${days}`,
        {}
      );
      addToast({ title: `${data?.count ?? 0} lembrete(s) enviado(s)`, type: 'success' });
      onClose();
    } catch (err) {
      addToast({ title: 'Erro ao enviar lote', type: 'error' });
      handleApiErrors(err);
    } finally {
      setBusy(null);
    }
  };

  const canSend = preview !== null && preview.days === days && preview.count > 0;

  return (
    <Container>
      <Header>
        <div>
          <h3>Enviar lote — Lembrete pré-vencimento</h3>
          <p>Dispara o lembrete para todas as faturas em aberto com vencimento próximo.</p>
        </div>
        <button onClick={onClose}><FiX size={16} /></button>
      </Header>
      <Body>
        <Notice>
          Envio <strong>em massa</strong> para os alunos. Simule antes para ver quantas faturas
          entram na janela; o botão de envio só libera após a simulação.
        </Notice>

        <Field>
          <Label>Dias de antecedência</Label>
          <Input
            type="number"
            min={0}
            value={days}
            onChange={e => handleDaysChange(Number(e.target.value))}
            disabled={busy !== null}
          />
          <Hint>Faturas com vencimento daqui a exatamente esse número de dias e status "aguardando".</Hint>
        </Field>

        {preview && preview.days === days && (
          <PreviewBox>
            <strong>{preview.count}</strong> fatura(s) receberão o lembrete
            <div className="sub">Vencimento daqui a {days} dia(s).</div>
          </PreviewBox>
        )}
      </Body>
      <Footer>
        <BtnCancel onClick={onClose} disabled={busy !== null}>Cancelar</BtnCancel>
        <BtnCancel onClick={handleSimulate} disabled={busy !== null}>
          {busy === 'sim' ? 'Simulando…' : 'Simular'}
        </BtnCancel>
        <BtnPrimary onClick={handleSend} disabled={!canSend || busy !== null}>
          <FiSend size={13} />
          {busy === 'send'
            ? 'Enviando…'
            : canSend
              ? `Enviar (${preview?.count})`
              : 'Enviar'}
        </BtnPrimary>
      </Footer>
    </Container>
  );
};

// =========================================================
// Botão — Teste (default, vai em headModules)
// =========================================================
export default function SendInvoiceTestMail(): JSX.Element {
  const { addModal, removeModal } = useModal();

  const open = () => {
    addModal({
      key: 'send-invoice-test-mail',
      theme: 'whiteModal',
      title: '',
      content: <TestModal onClose={() => removeModal('send-invoice-test-mail')} />
    });
  };

  return (
    <button
      className="searchButton"
      title="Enviar e-mail de teste"
      type="button"
      onClick={open}
    >
      <FaPaperPlane />
    </button>
  );
}

// =========================================================
// Botão — Lote pré-vencimento (export nomeado, vai em headModules)
// =========================================================
export function SendBulkReminders(): JSX.Element {
  const { addModal, removeModal } = useModal();

  const open = () => {
    addModal({
      key: 'send-bulk-reminders',
      theme: 'whiteModal',
      title: '',
      content: <BulkReminderModal onClose={() => removeModal('send-bulk-reminders')} />
    });
  };

  return (
    <button
      className="searchButton"
      title="Enviar lote de lembrete pré-vencimento"
      type="button"
      onClick={open}
    >
      <FiBell />
    </button>
  );
}
